import { useState, useEffect } from 'react';
import {
  MAINTENANCE_STATUS_URL,
  MAINTENANCE_CHECK_TIMEOUT_MS,
} from 'Constants/maintenanceConfig';

interface MaintenanceState {
  isLoading: boolean;
  isMaintenance: boolean;
}

/**
 * Fetches the system-status.json endpoint ONCE on mount to determine whether
 * the application is in maintenance mode.
 *
 * Precedence rules (evaluated in order):
 *
 *  Priority 1 — REACT_APP_MAINTENANCE_STATUS_URL (this hook)
 *    If the env var is set and the URL returns { "maintenanceMode": true }, the app
 *    renders <MaintenancePage> immediately. Azure Entra auth never fires.
 *    This always takes precedence over the colleague's mechanism.
 *
 *  Priority 2 — REACT_APP_MAINTENANCE_MODE / REACT_APP_PAGES_UNDER_MAINTENANCE
 *    Evaluated only when Priority 1 is inactive (URL not configured, fetch
 *    fails, or response contains { "status": false }). Handled post-auth
 *    inside Landing.tsx and AppRoutes.tsx.
 *
 *  Priority 3 — Normal application flow.
 *
 * Fail-open policy: any network error, timeout, non-2xx response, or malformed
 * JSON body resolves as isMaintenance = false so users are never blocked by an
 * unreachable status endpoint.
 */
export function useMaintenanceCheck(): MaintenanceState {
  const [state, setState] = useState<MaintenanceState>({
    isLoading: !!MAINTENANCE_STATUS_URL, // skip loading state when mechanism is disabled
    isMaintenance: false,
  });

  useEffect(() => {
    // If no URL is configured for this environment, skip the check entirely.
    if (!MAINTENANCE_STATUS_URL) return;
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      MAINTENANCE_CHECK_TIMEOUT_MS
    );

    const checkStatus = async () => {
      try {
        const response = await fetch(MAINTENANCE_STATUS_URL as string, {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store', // always fetch fresh; never serve stale maintenance state
        });

        if (!response.ok) {
          // Non-2xx (e.g. 403/404 on the blob) → fail open
          if (!cancelled) setState({ isLoading: false, isMaintenance: false });
          return;
        }

        const data = await response.json();
        const isMaintenance = data?.maintenanceMode === true;

        if (!cancelled) setState({ isLoading: false, isMaintenance });
      } catch {
        // AbortError (timeout), NetworkError, JSON parse failure → fail open
        if (!cancelled) setState({ isLoading: false, isMaintenance: false });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []); // intentional empty deps — run only on app mount

  return state;
}
